(() => {
  const root = typeof window !== 'undefined' ? window : globalThis;
  const MOBILE_QUERY = '(max-width: 749px)';
  const DRAG_THRESHOLD = 6;
  const SUPPRESSED_CLICK_RESET_DELAY = 50;
  const VIEWPORT_MARGIN = 8;
  const BACK_TO_TOP_THRESHOLD_RATIO = 1;
  const SCROLL_HIDE_DELTA = 24;
  const SCROLL_IDLE_DELAY = 700;
  const AVOID_CONTROL_SELECTOR = '[data-floating-widget-avoid]';
  const FORMS_HOST_SELECTOR = 'shopify-forms-embed';
  const FORMS_READY_RETRY_DELAY = 500;
  const FORMS_READY_RETRY_LIMIT = 10;
  const FORMS_SURFACE_SELECTOR = '#app-embed';
  const FORMS_DRAG_TARGET_SELECTOR = '[data-testid="form-teaser"]';
  const FORMS_CLOSE_CONTROL_SELECTOR = '[aria-label="Close modal"]';
  const STORAGE_PREFIX = 'jft-floating-widget-position:';
  let registered = false;

  function registerFloatingWidgetManager() {
    if (!root.Alpine || registered) {
      return;
    }

    registered = true;
    root.Alpine.data('floatingWidgetManager', () => ({
      boundWidgets: new Map(),
      avoidedWidgetHosts: new Set(),
      focusHandler: null,
      formsMutationObserver: null,
      formsObservedRoot: null,
      formsReadyAttempts: 0,
      mutationObserver: null,
      resizeHandler: null,
      scrollHandler: null,
      syncScheduled: false,
      widgetReadyTimer: null,
      backToTopVisible: false,
      actionsTucked: false,
      lastScrollY: Math.max(root.scrollY || 0, 0),
      scrollIdleTimer: null,

      init() {
        this.resizeHandler = () => this.handleViewportChange();
        this.scrollHandler = () => {
          this.trackScroll();
          this.scheduleSync();
        };
        this.trackScroll();
        this.focusHandler = () => this.scheduleSync();
        this.mutationObserver = new MutationObserver(() => this.scheduleSync());
        this.mutationObserver.observe(root.document.documentElement, {
          childList: true,
          subtree: true,
        });

        root.addEventListener('resize', this.resizeHandler, { passive: true });
        root.addEventListener('scroll', this.scrollHandler, { passive: true });
        root.document.addEventListener('focusin', this.focusHandler);
        this.syncWidgets();
      },

      destroy() {
        this.mutationObserver?.disconnect();
        this.formsMutationObserver?.disconnect();
        root.clearTimeout(this.widgetReadyTimer);
        root.clearTimeout(this.scrollIdleTimer);
        root.removeEventListener('resize', this.resizeHandler);
        root.removeEventListener('scroll', this.scrollHandler);
        root.document.removeEventListener('focusin', this.focusHandler);

        this.boundWidgets.forEach((binding, element) => {
          this.unbindWidget(element, binding);
        });
        this.boundWidgets.clear();
        this.avoidedWidgetHosts.forEach((host) => {
          host.classList.remove('jft-floating-widget--avoids-controls');
        });
        this.avoidedWidgetHosts.clear();
        this.formsObservedRoot = null;
      },

      isMobile() {
        return root.matchMedia(MOBILE_QUERY).matches;
      },

      trackScroll() {
        const scrollY = Math.max(root.scrollY || 0, 0);
        const viewportHeight = root.innerHeight || 0;
        this.backToTopVisible = scrollY > viewportHeight * BACK_TO_TOP_THRESHOLD_RATIO;

        if (!this.isMobile()) {
          this.actionsTucked = false;
          this.lastScrollY = scrollY;
          return;
        }

        const delta = scrollY - this.lastScrollY;
        if (delta > SCROLL_HIDE_DELTA) {
          this.actionsTucked = true;
          this.lastScrollY = scrollY;
        } else if (delta < -SCROLL_HIDE_DELTA || scrollY <= 0) {
          this.actionsTucked = false;
          this.lastScrollY = scrollY;
        }

        root.clearTimeout(this.scrollIdleTimer);
        this.scrollIdleTimer = root.setTimeout(() => {
          this.scrollIdleTimer = null;
          this.actionsTucked = false;
        }, SCROLL_IDLE_DELAY);
      },

      backToTop() {
        root.dispatchEvent(new CustomEvent('site:back-to-top'));
        const reduceMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        root.scrollTo({
          top: 0,
          left: 0,
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      },

      scheduleSync() {
        if (this.syncScheduled) {
          return;
        }

        this.syncScheduled = true;
        root.requestAnimationFrame(() => {
          this.syncScheduled = false;
          this.syncWidgets();
        });
      },

      syncWidgets() {
        const rewardsLauncher = root.document.querySelector('#ba-loy-container #launcher-wrapper');
        if (rewardsLauncher) {
          this.bindWidget(rewardsLauncher, 'rivo-rewards');
        }

        const formsHost = root.document.querySelector(FORMS_HOST_SELECTOR);
        if (formsHost) {
          this.avoidedWidgetHosts.add(formsHost);
          this.observeFormsRoot(formsHost.shadowRoot);
          this.syncAvoidedControls(formsHost);

          const formsSurface = formsHost.shadowRoot?.querySelector(FORMS_SURFACE_SELECTOR);
          const formsLauncher = formsSurface?.querySelector(FORMS_DRAG_TARGET_SELECTOR);
          if (formsSurface && formsLauncher) {
            this.formsReadyAttempts = 0;
            this.bindWidget(formsSurface, 'shopify-forms');
          } else {
            const formsBinding = formsSurface && this.boundWidgets.get(formsSurface);
            if (formsBinding?.key === 'shopify-forms') {
              this.unbindWidget(formsSurface, formsBinding);
              this.boundWidgets.delete(formsSurface);
            }
            if (!formsSurface) {
              this.scheduleWidgetReadySync();
            }
          }
        }

        this.boundWidgets.forEach((binding, element) => {
          if (!element.isConnected) {
            this.unbindWidget(element, binding);
            this.boundWidgets.delete(element);
          }
        });

        this.avoidedWidgetHosts.forEach((host) => {
          if (!host.isConnected) {
            host.classList.remove('jft-floating-widget--avoids-controls');
            this.avoidedWidgetHosts.delete(host);
          }
        });
      },

      observeFormsRoot(shadowRoot) {
        if (!shadowRoot || this.formsObservedRoot === shadowRoot) {
          return;
        }

        this.formsMutationObserver?.disconnect();
        this.formsObservedRoot = shadowRoot;
        this.formsMutationObserver = new MutationObserver(() => this.scheduleSync());
        this.formsMutationObserver.observe(shadowRoot, {
          childList: true,
          subtree: true,
        });
      },

      syncAvoidedControls(host) {
        const surface = host.shadowRoot?.querySelector(FORMS_SURFACE_SELECTOR);
        if (!surface) {
          host.classList.remove('jft-floating-widget--avoids-controls');
          this.scheduleWidgetReadySync();
          return;
        }

        this.formsReadyAttempts = 0;
        root.clearTimeout(this.widgetReadyTimer);
        this.widgetReadyTimer = null;
        const widgetRect = surface.getBoundingClientRect();
        const overlapsControl = Array.from(
          root.document.querySelectorAll(AVOID_CONTROL_SELECTOR),
        ).some((control) => {
          const controlRect = control.getBoundingClientRect();
          if (!controlRect.width || !controlRect.height) {
            return false;
          }

          return this.rectsOverlap(widgetRect, controlRect);
        });

        host.classList.toggle(
          'jft-floating-widget--avoids-controls',
          overlapsControl,
        );
      },

      scheduleWidgetReadySync() {
        if (this.widgetReadyTimer || this.formsReadyAttempts >= FORMS_READY_RETRY_LIMIT) {
          return;
        }

        this.widgetReadyTimer = root.setTimeout(() => {
          this.widgetReadyTimer = null;
          this.formsReadyAttempts += 1;
          this.syncWidgets();
        }, FORMS_READY_RETRY_DELAY);
      },

      rectsOverlap(first, second) {
        return (
          first.width > 0
          && first.height > 0
          && first.left < second.right
          && first.right > second.left
          && first.top < second.bottom
          && first.bottom > second.top
        );
      },

      bindWidget(element, key) {
        if (this.boundWidgets.has(element)) {
          return;
        }

        const binding = {
          drag: null,
          key,
          restoreTimer: null,
          suppressClick: false,
          suppressClickTimer: null,
          viewport: null,
        };

        binding.pointerDown = (event) => this.startDrag(event, element, binding);
        binding.pointerMove = (event) => this.moveDrag(event, element, binding);
        binding.pointerUp = (event) => this.finishDrag(event, element, binding, true);
        binding.pointerCancel = (event) => this.finishDrag(event, element, binding, false);
        binding.click = (event) => {
          if (!binding.suppressClick) {
            return;
          }

          binding.suppressClick = false;
          root.clearTimeout(binding.suppressClickTimer);
          binding.suppressClickTimer = null;
          event.preventDefault();
          event.stopImmediatePropagation();
        };

        element.addEventListener('pointerdown', binding.pointerDown, true);
        root.document.addEventListener('pointermove', binding.pointerMove, { capture: true, passive: false });
        root.document.addEventListener('pointerup', binding.pointerUp, true);
        root.document.addEventListener('pointercancel', binding.pointerCancel, true);
        element.addEventListener('click', binding.click, true);
        this.boundWidgets.set(element, binding);
        this.prepareWidgetForViewport(element, binding);
      },

      unbindWidget(element, binding) {
        root.clearTimeout(binding.restoreTimer);
        root.clearTimeout(binding.suppressClickTimer);
        element.removeEventListener('pointerdown', binding.pointerDown, true);
        root.document.removeEventListener('pointermove', binding.pointerMove, true);
        root.document.removeEventListener('pointerup', binding.pointerUp, true);
        root.document.removeEventListener('pointercancel', binding.pointerCancel, true);
        element.removeEventListener('click', binding.click, true);
        element.classList.remove(
          'jft-draggable-widget',
          'jft-draggable-widget--dragging'
        );
        element.style.removeProperty('cursor');
        element.style.removeProperty('touch-action');
        element.style.removeProperty('translate');
        element.style.removeProperty('user-select');
      },

      prepareWidgetForViewport(element, binding) {
        const viewport = this.isMobile() ? 'mobile' : 'desktop';
        if (binding.viewport !== viewport) {
          binding.viewport = viewport;
          element.style.removeProperty('translate');
          delete element.dataset.jftDragX;
          delete element.dataset.jftDragY;
        }

        element.classList.add('jft-draggable-widget');
        element.style.setProperty('cursor', 'grab', 'important');
        element.style.setProperty('touch-action', 'none', 'important');
        element.style.setProperty('user-select', 'none', 'important');
        this.schedulePositionRestore(element, binding);
      },

      schedulePositionRestore(element, binding) {
        root.clearTimeout(binding.restoreTimer);
        binding.restoreTimer = root.setTimeout(() => {
          binding.restoreTimer = null;
          if (element.isConnected && !binding.drag) {
            this.restorePosition(element, binding.key);
          }
        }, 400);
      },

      handleViewportChange() {
        this.boundWidgets.forEach((binding, element) => {
          this.prepareWidgetForViewport(element, binding);
        });
        this.scheduleSync();
      },

      startDrag(event, element, binding) {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }

        if (
          binding.key === 'shopify-forms'
          && event.target?.closest?.(FORMS_CLOSE_CONTROL_SELECTOR)
        ) {
          return;
        }

        root.clearTimeout(binding.restoreTimer);
        binding.restoreTimer = null;
        const rect = element.getBoundingClientRect();
        binding.drag = {
          baseX: Number(element.dataset.jftDragX || 0),
          baseY: Number(element.dataset.jftDragY || 0),
          height: rect.height,
          moved: false,
          pointerId: event.pointerId,
          startLeft: rect.left,
          startTop: rect.top,
          startX: event.clientX,
          startY: event.clientY,
          width: rect.width,
        };

        if (binding.key !== 'shopify-forms') {
          element.setPointerCapture?.(event.pointerId);
        }
      },

      moveDrag(event, element, binding) {
        const drag = binding.drag;
        if (!drag || drag.pointerId !== event.pointerId) {
          return;
        }

        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;
        if (!drag.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD) {
          return;
        }

        drag.moved = true;
        event.preventDefault();
        event.stopImmediatePropagation();

        const maxLeft = Math.max(VIEWPORT_MARGIN, root.innerWidth - drag.width - VIEWPORT_MARGIN);
        const maxTop = Math.max(VIEWPORT_MARGIN, root.innerHeight - drag.height - VIEWPORT_MARGIN);
        const nextLeft = this.clamp(drag.startLeft + deltaX, VIEWPORT_MARGIN, maxLeft);
        const nextTop = this.clamp(drag.startTop + deltaY, VIEWPORT_MARGIN, maxTop);
        const nextX = drag.baseX + nextLeft - drag.startLeft;
        const nextY = drag.baseY + nextTop - drag.startTop;

        this.applyOffset(element, nextX, nextY);
        element.classList.add('jft-draggable-widget--dragging');
        element.style.setProperty('cursor', 'grabbing', 'important');
      },

      finishDrag(event, element, binding, persist) {
        const drag = binding.drag;
        if (!drag || drag.pointerId !== event.pointerId) {
          return;
        }

        if (drag.moved) {
          root.clearTimeout(binding.suppressClickTimer);
          binding.suppressClick = true;
          binding.suppressClickTimer = root.setTimeout(() => {
            binding.suppressClick = false;
            binding.suppressClickTimer = null;
          }, SUPPRESSED_CLICK_RESET_DELAY);
          event.preventDefault();
          event.stopImmediatePropagation();
          if (persist) {
            this.savePosition(element, binding.key);
            this.scheduleSync();
          }
        }

        if (binding.key !== 'shopify-forms') {
          element.releasePointerCapture?.(event.pointerId);
        }
        element.classList.remove('jft-draggable-widget--dragging');
        element.style.setProperty('cursor', 'grab', 'important');
        binding.drag = null;
      },

      applyOffset(element, x, y) {
        const roundedX = Math.round(x);
        const roundedY = Math.round(y);
        element.dataset.jftDragX = String(roundedX);
        element.dataset.jftDragY = String(roundedY);
        element.style.setProperty('translate', `${roundedX}px ${roundedY}px`, 'important');
      },

      savePosition(element, key) {
        const rect = element.getBoundingClientRect();
        try {
          root.localStorage.setItem(
            this.storageKey(key),
            JSON.stringify({ left: Math.round(rect.left), top: Math.round(rect.top) })
          );
        } catch (_error) {
          // Dragging remains available when storage is blocked.
        }
      },

      restorePosition(element, key) {
        let savedPosition = null;
        try {
          savedPosition = JSON.parse(root.localStorage.getItem(this.storageKey(key)));
        } catch (_error) {
          savedPosition = null;
        }

        if (!savedPosition || !Number.isFinite(savedPosition.left) || !Number.isFinite(savedPosition.top)) {
          return;
        }

        element.style.removeProperty('translate');
        delete element.dataset.jftDragX;
        delete element.dataset.jftDragY;

        const rect = element.getBoundingClientRect();
        const maxLeft = Math.max(VIEWPORT_MARGIN, root.innerWidth - rect.width - VIEWPORT_MARGIN);
        const maxTop = Math.max(VIEWPORT_MARGIN, root.innerHeight - rect.height - VIEWPORT_MARGIN);
        const targetLeft = this.clamp(savedPosition.left, VIEWPORT_MARGIN, maxLeft);
        const targetTop = this.clamp(savedPosition.top, VIEWPORT_MARGIN, maxTop);
        this.applyOffset(element, targetLeft - rect.left, targetTop - rect.top);
      },

      storageKey(key) {
        if (this.isMobile()) {
          return `${STORAGE_PREFIX}${key}`;
        }

        return `${STORAGE_PREFIX}${key}:desktop`;
      },

      clamp(value, minimum, maximum) {
        return Math.min(Math.max(value, minimum), maximum);
      },
    }));
  }

  if (root.Alpine) {
    registerFloatingWidgetManager();
  } else {
    root.document.addEventListener('alpine:init', registerFloatingWidgetManager, { once: true });
  }
})();
